'use client';

import { useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { useSkinEditor } from './SkinEditorProvider';
import { MAX_LAYERS, MAX_LAYER_NAME, translucentBaseWarning, mergeDownProblem, type SkinLayer } from '@/app/lib/skinEditor/layers';

// Loaded only when opened (it pulls in the parts library and, on its Default tab, Mojang's skins).
const ImportPartDialog = dynamic(() => import('./ImportPartDialog'), { ssr: false });

/**
 * The layer stack. Rendered on the SERVER too (not a dynamic import), and every part of it has a
 * FIXED height — the list scrolls inside a fixed box and the status line is always reserved — so
 * neither a restored multi-layer session nor the "restored" notice can shift the page below it.
 */

const CHECKER_BG = {
  backgroundImage: 'repeating-conic-gradient(#3a3a3a 0% 25%, #2a2a2a 0% 50%)',
  backgroundSize: '8px 8px',
} as const;

function LayerThumb({ pixels }: { pixels: Uint8ClampedArray }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const ctx = ref.current?.getContext('2d');
    if (!ctx) return;
    ctx.putImageData(new ImageData(new Uint8ClampedArray(pixels), 64, 64), 0, 0);
  }, [pixels]);
  return (
    <canvas
      ref={ref}
      width={64}
      height={64}
      aria-hidden="true"
      className="w-7 h-7 flex-shrink-0 rounded border border-zinc-700"
      style={{ ...CHECKER_BG, imageRendering: 'pixelated' }}
    />
  );
}

const ICON = 'w-4 h-4';
const ICON_BTN =
  'w-7 h-7 flex items-center justify-center rounded-md text-zinc-400 hover:bg-zinc-700 hover:text-white transition-colors disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-zinc-400 disabled:cursor-not-allowed';

function Icon({ d }: { d: string }) {
  return (
    <svg className={ICON} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={d} />
    </svg>
  );
}

const PATHS = {
  add: 'M12 5v14M5 12h14',
  duplicate: 'M8 8h11v11H8zM5 16V5h11',
  rename: 'M15.232 5.232l3.536 3.536M4 20h4L18.5 9.5l-4-4L4 16v4z',
  mergeDown: 'M12 4v12m0 0l-4-4m4 4l4-4M5 20h14',
  flatten: 'M4 7l8-4 8 4-8 4-8-4zM4 12l8 4 8-4M4 17l8 4 8-4',
  delete: 'M6 7h12M9 7V4h6v3m-8 0l1 13h8l1-13',
  eye: 'M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12zm10 3a3 3 0 100-6 3 3 0 000 6z',
  eyeOff: 'M3 3l18 18M10.6 10.6a2 2 0 002.8 2.8M9.9 5.1A10.4 10.4 0 0112 5c6.5 0 10 7 10 7a17.6 17.6 0 01-3.2 4.2M6.6 6.6C3.9 8.4 2 12 2 12s3.5 7 10 7a9.8 9.8 0 005.4-1.6',
  lock: 'M7 11V8a5 5 0 0110 0v3M5 11h14v10H5z',
  unlock: 'M7 11V8a5 5 0 019.9-1M5 11h14v10H5z',
  importPart: 'M12 3v10m0 0l-4-4m4 4l4-4M4 15v3a2 2 0 002 2h12a2 2 0 002-2v-3',
  up: 'M5 15l7-7 7 7',
  down: 'M19 9l-7 7-7-7',
};

function LayerRow({
  layer, index, count, active, editing, onStartEdit, onEndEdit,
}: {
  layer: SkinLayer;
  index: number;
  count: number;
  active: boolean;
  editing: boolean;
  onStartEdit: () => void;
  onEndEdit: () => void;
}) {
  const { selectLayer, setLayerVisible, setLayerLocked, moveLayer, renameLayer } = useSkinEditor();
  // Uncontrolled: the input starts from the CURRENT name every time editing opens.
  const inputRef = useRef<HTMLInputElement>(null);
  const cancelledRef = useRef(false);

  const commitName = () => {
    const draft = inputRef.current?.value ?? '';
    if (!cancelledRef.current && draft.trim() && draft !== layer.name) renameLayer(layer.id, draft);
    cancelledRef.current = false;
    onEndEdit();
  };

  return (
    <li
      aria-current={active ? 'true' : undefined}
      className={`flex items-center gap-1 rounded-lg border px-1 py-0.5 ${
        active ? 'bg-purple-600/20 border-purple-500/60' : 'bg-zinc-800/50 border-transparent hover:border-zinc-700'
      }`}
    >
      <button
        type="button"
        onClick={() => setLayerVisible(layer.id, !layer.visible)}
        className={ICON_BTN}
        title={layer.visible ? 'Hide layer' : 'Show layer'}
        aria-label={`${layer.visible ? 'Hide' : 'Show'} ${layer.name}`}
        aria-pressed={!layer.visible}
      >
        <Icon d={layer.visible ? PATHS.eye : PATHS.eyeOff} />
      </button>
      <LayerThumb pixels={layer.pixels} />
      {editing ? (
        <input
          ref={inputRef}
          autoFocus
          defaultValue={layer.name}
          maxLength={MAX_LAYER_NAME}
          onFocus={(e) => e.currentTarget.select()}
          onBlur={commitName}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
            if (e.key === 'Escape') { cancelledRef.current = true; e.currentTarget.blur(); }
          }}
          aria-label="Layer name"
          className="flex-1 min-w-0 bg-zinc-950 border border-zinc-700 rounded px-1.5 py-0.5 text-xs text-white focus:border-violet-500 focus:outline-none"
        />
      ) : (
        <button
          type="button"
          onClick={() => selectLayer(layer.id)}
          onDoubleClick={onStartEdit}
          className={`flex-1 min-w-0 text-left text-xs px-1 py-1 truncate ${layer.visible ? 'text-zinc-200' : 'text-zinc-500 italic'}`}
          title={`${layer.name}${layer.opacity < 1 ? ` · ${Math.round(layer.opacity * 100)}%` : ''} — double-click to rename`}
        >
          {layer.name}
          {layer.opacity < 1 && <span className="text-zinc-500"> · {Math.round(layer.opacity * 100)}%</span>}
        </button>
      )}
      <button
        type="button"
        onClick={() => setLayerLocked(layer.id, !layer.locked)}
        className={`${ICON_BTN} ${layer.locked ? 'text-amber-400' : ''}`}
        title={layer.locked ? 'Unlock layer' : 'Lock layer (no painting)'}
        aria-label={`${layer.locked ? 'Unlock' : 'Lock'} ${layer.name}`}
        aria-pressed={layer.locked}
      >
        <Icon d={layer.locked ? PATHS.lock : PATHS.unlock} />
      </button>
      <button
        type="button"
        onClick={() => moveLayer(layer.id, 1)}
        disabled={index === count - 1}
        className={ICON_BTN}
        title="Move up"
        aria-label={`Move ${layer.name} up`}
      >
        <Icon d={PATHS.up} />
      </button>
      <button
        type="button"
        onClick={() => moveLayer(layer.id, -1)}
        disabled={index === 0}
        className={ICON_BTN}
        title="Move down"
        aria-label={`Move ${layer.name} down`}
      >
        <Icon d={PATHS.down} />
      </button>
    </li>
  );
}

export default function LayersPanel() {
  const {
    state, composite, activeLayer, addLayer, duplicateLayer, deleteLayer, mergeDown, flatten, setLayerOpacity,
    restoredSession, dismissRestored, startNew, autosaveFailed,
  } = useSkinEditor();
  // Base pixels below full alpha are drawn SOLID in game (empty ones black) — the 3D preview shows that, the 2D checkerboard does not.
  const baseWarning = translucentBaseWarning(composite, state.modelType);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const gestureRef = useRef(0);

  const layers = state.doc.layers;
  const count = layers.length;
  const activeIndex = layers.findIndex(l => l.id === activeLayer.id);
  const full = count >= MAX_LAYERS;
  const mergeProblem = activeIndex >= 0 ? mergeDownProblem(state.doc, activeLayer.id) : 'No active layer';

  return (
    <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-3">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-sm text-zinc-400" title={`${count} of ${MAX_LAYERS} layers`}>Layers</div>
        <div className="flex items-center gap-0.5">
          {/* Import part: body parts of another skin as a new layer. Labelled from sm; an icon on a
              phone, where the header has no room for the words. Same row height either way. */}
          <button
            type="button"
            onClick={() => setImportOpen(true)}
            disabled={full}
            className={`${ICON_BTN} sm:w-auto sm:px-2 sm:gap-1 sm:text-xs`}
            title={full ? `Import part (the stack is full: ${MAX_LAYERS} layers)` : 'Import part — take the head, hair, outfit… of another skin as a new layer'}
            aria-label="Import part from another skin"
            aria-haspopup="dialog"
          >
            <Icon d={PATHS.importPart} />
            <span className="hidden sm:inline">Import part</span>
          </button>
          <button type="button" onClick={addLayer} disabled={full} className={ICON_BTN} title="New layer" aria-label="New layer">
            <Icon d={PATHS.add} />
          </button>
          <button type="button" onClick={() => duplicateLayer(activeLayer.id)} disabled={full} className={ICON_BTN} title="Duplicate layer" aria-label="Duplicate layer">
            <Icon d={PATHS.duplicate} />
          </button>
          <button type="button" onClick={() => setEditingId(activeLayer.id)} className={ICON_BTN} title="Rename layer" aria-label="Rename layer">
            <Icon d={PATHS.rename} />
          </button>
          <button
            type="button"
            onClick={() => mergeDown(activeLayer.id)}
            disabled={mergeProblem !== null}
            className={ICON_BTN}
            title={mergeProblem ? `Merge down (${mergeProblem.toLowerCase()})` : 'Merge down'}
            aria-label="Merge down"
          >
            <Icon d={PATHS.mergeDown} />
          </button>
          <button type="button" onClick={flatten} disabled={count < 2} className={ICON_BTN} title="Flatten all layers" aria-label="Flatten all layers">
            <Icon d={PATHS.flatten} />
          </button>
          <button
            type="button"
            onClick={() => deleteLayer(activeLayer.id)}
            disabled={count < 2}
            className={`${ICON_BTN} hover:text-red-400`}
            title="Delete layer"
            aria-label="Delete layer"
          >
            <Icon d={PATHS.delete} />
          </button>
        </div>
      </div>

      {/* Top of the stack first, like every paint program. Fixed height: see the note above. */}
      <ul aria-label="Layers" className="h-[132px] overflow-y-auto space-y-1 pr-0.5">
        {layers.map((layer, i) => ({ layer, i })).reverse().map(({ layer, i }) => (
          <LayerRow
            key={layer.id}
            layer={layer}
            index={i}
            count={count}
            active={layer.id === activeLayer.id}
            editing={editingId === layer.id}
            onStartEdit={() => setEditingId(layer.id)}
            onEndEdit={() => setEditingId(null)}
          />
        ))}
      </ul>

      <label className="mt-2 flex items-center gap-2 text-xs text-zinc-400">
        <span className="flex-shrink-0">Opacity</span>
        <input
          type="range"
          min={0}
          max={100}
          value={Math.round(activeLayer.opacity * 100)}
          onPointerDown={() => { gestureRef.current += 1; }}
          onKeyDown={() => { gestureRef.current += 1; }}
          onChange={(e) => setLayerOpacity(activeLayer.id, Number(e.target.value) / 100, gestureRef.current)}
          className="flex-1 min-w-0 h-2 accent-purple-500 cursor-pointer"
          aria-label={`Opacity of ${activeLayer.name}`}
        />
        <span className="w-9 text-right font-mono text-zinc-300">{Math.round(activeLayer.opacity * 100)}%</span>
      </label>

      {importOpen && <ImportPartDialog open onClose={() => setImportOpen(false)} />}

      {/* Status line — always reserved (h-5) so showing a notice never moves anything. */}
      <div className="mt-1 h-5 flex items-center gap-2 text-xs min-w-0" aria-live="polite">
        {restoredSession ? (
          <>
            <span className="text-zinc-400 truncate">Restored your last session</span>
            <span className="text-zinc-600" aria-hidden="true">·</span>
            <button type="button" onClick={startNew} className="text-purple-300 hover:text-purple-200 underline-offset-2 hover:underline flex-shrink-0">
              Start new
            </button>
            <button type="button" onClick={dismissRestored} className="ml-auto text-zinc-500 hover:text-zinc-300 flex-shrink-0" aria-label="Dismiss">
              ×
            </button>
          </>
        ) : autosaveFailed ? (
          <span className="text-amber-400 truncate" title="This browser is not letting the page store your work. Save or download your skin before leaving.">
            Autosave off — download before leaving
          </span>
        ) : baseWarning ? (
          <span
            className="text-amber-400/90 truncate"
            title={`${baseWarning} The inner (base) layer is always solid in Minecraft: an empty pixel shows black and a see-through one at full strength. Paint every base pixel fully opaque, or check the 3D preview, which shows it the way the game does. (Skins from the old editor had a gray checkerboard there — it is removed on import, so those pixels are empty too.)`}
          >
            {baseWarning}
          </span>
        ) : (
          <span className="text-zinc-500 truncate">Changes autosave in this browser</span>
        )}
      </div>
    </div>
  );
}
